import React, { useContext, useEffect, useState } from 'react';
import { SwipeableDrawer, IconButton, Typography, Box, Skeleton, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { NowPlayingContext } from './NowPlayingProvider';
import CustomProgressBar from './CustomProgressBar';
import FlashingLiveIndicator from './FlashingLiveIndicator';
import { useSwipeable } from 'react-swipeable';

const AudioDrawer = ({ open, onOpen = () => {}, onClose, nowPlaying }) => {
  const {
    isPlaying,
    startPlayback,
    pauseTrack,
    nowPlayingData,
    playingNext,
    isLiveStream,
    progressColor,
    isLoading,
    isLive,
    initialLoadComplete,
    station,
    playPreviousTrack, playNextTrack, currentTrackIndex,
    loadingTrackId,
  } = useContext(NowPlayingContext);

  const [elapsedTime, setElapsedTime] = useState(nowPlayingData?.elapsed || 0);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (!isLiveStream) playNextTrack(nowPlayingData?.trackList || []);
    },
    onSwipedRight: () => {
      if (!isLiveStream) playPreviousTrack(nowPlayingData?.trackList || []);
    },
    preventScrollOnSwipe: true, // Prevent scrolling during swipe gestures
    trackMouse: !isDesktop, // Avoid swipe-skip misfiring while dragging the desktop scrub rail with a mouse
  });

  useEffect(() => {
    let timer;

    if (isPlaying && !isLiveStream) {
      timer = setInterval(() => {
        setElapsedTime((prev) => Math.min(prev + 1, nowPlayingData?.duration || 0));
      }, 1000);
    } else {
      clearInterval(timer);
    }

    return () => clearInterval(timer);
  }, [isPlaying, isLiveStream, nowPlayingData?.duration]);

  useEffect(() => {
    setElapsedTime(nowPlayingData?.elapsed || 0);
  }, [nowPlayingData?.elapsed]);

  // isLoading also flips true on a station switch, even though playingNext still holds the
  // previous station's stale value - treat that the same as "waiting for data" so this
  // doesn't show the old station's up-next info while the new one loads.
  const showUpNextSkeleton = isLoading || (!playingNext && !initialLoadComplete);
  const showUpNext = isLiveStream && !isLive && (playingNext || showUpNextSkeleton);

  const togglePlayback = () => {
    if (isPlaying) {
      pauseTrack();
    } else {
      startPlayback();
    }
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      {...swipeHandlers} // Attach swipe handlers to the drawer
      PaperProps={{
        sx: {
          // Suppress the previous station's art while loading so it doesn't linger as a
          // stale backdrop during a station switch - the Skeleton box below fills the gap.
          backgroundImage: { xs: (!isLoading && nowPlaying?.art) ? `url(${nowPlaying.art})` : 'none', md: 'none' },
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPositionX: `${(elapsedTime / (nowPlayingData?.duration || 1)) * 100}%`,
          backgroundPositionY: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          transition: 'background-position 0.1s linear', // Smooth movement
        },
      }}
    >
      {/* Desktop backdrop (md+) - sharp artwork, darkened by the gradient layer below */}
      {isLoading ? (
        <Skeleton
          variant="rectangular"
          sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', inset: 0, zIndex: 0 }}
        />
      ) : (
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            backgroundImage: nowPlaying?.art ? `url(${nowPlaying.art})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}

      {/* Mobile layout (xs) - unchanged from original design */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'relative',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Backdrop skeleton fills the gap left by suppressing the stale backgroundImage above while loading */}
        {isLoading && (
          <Skeleton
            variant="rectangular"
            sx={{ position: 'absolute', inset: 0, zIndex: 0 }}
          />
        )}

        {/* Progress Container Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0.4,
            zIndex: 1,
            '& .rhap_progress-container': {
              height: '100% !important',
              bottom: '0 !important',
              backgroundColor: '#363636 !important',
            },
          }}
        >
          <CustomProgressBar
            key="audio-drawer-progress-bar"
            initialElapsed={nowPlayingData?.elapsed || 0}
            duration={nowPlayingData?.duration || 1}
            color={
              isLiveStream
                ? isLive
                  ? 'rgba(0, 0, 0, 0)' // isLiveStream is true and isLive is true
                  : progressColor // isLiveStream is true and isLive is false
                : '#868686' // isLiveStream is false
            }
          />
        </Box>

        {/* Top Section */}
        <Box
          sx={{
            width: '100%',
            padding: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 2,
          }}
        >
          <Box>
            {isLoading ? (
              <>
                <Skeleton
                  variant="text"
                  sx={{
                    fontSize: '1.25rem',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    width: '90%',
                    minWidth: '150px',
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    fontSize: '1rem',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    width: '60%',
                    maxWidth: '300px',
                    marginTop: '4px',
                  }}
                />
              </>
            ) : (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#fff',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    padding: '2px 8px',
                    borderRadius: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '300px',
                  }}
                >
                  {nowPlaying?.title || 'Unknown Title'}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#999',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    padding: '2px 8px',
                    borderRadius: 0,
                    marginTop: '4px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '300px',
                  }}
                >
                  {nowPlaying?.artist || 'Unknown Artist'}
                </Typography>
                {isLive && (
                  <Box sx={{ padding: '2px 0' }}>
                    <FlashingLiveIndicator station={station} />
                  </Box>
                )}
              </>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: '#fff',
              borderRadius: 0,
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        {/* Center Section */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            maxWidth: 400,
          }}
        >
          {loadingTrackId ? (
            <CircularProgress thickness={3} />
          ) : (
            <>
              {/* Left Box */}
              {!isLiveStream && (
                <Box
                  sx={{
                    flex: 1,
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    onClick={() => playPreviousTrack(nowPlayingData?.trackList || [])}
                    disabled={currentTrackIndex <= 0}
                  >
                    <SkipPreviousIcon sx={{ fontSize: 64 }} />
                  </IconButton>
                </Box>
              )}

              {/* Center Box */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <IconButton
                  onClick={togglePlayback}
                  sx={{
                    color: '#fff',
                  }}
                >
                  {isPlaying ? (
                    <PauseCircleIcon sx={{ fontSize: 64 }} />
                  ) : (
                    <PlayCircleIcon sx={{ fontSize: 64 }} />
                  )}
                </IconButton>
              </Box>

              {/* Right Box */}
              {!isLiveStream && (
                <Box
                  sx={{
                    flex: 1,
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    onClick={() => playNextTrack(nowPlayingData?.trackList || [])}
                    disabled={
                      !nowPlayingData?.trackList ||
                      currentTrackIndex === null ||
                      currentTrackIndex >= nowPlayingData.trackList.length - 1
                    }
                  >
                    <SkipNextIcon sx={{ fontSize: 64 }} />
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Desktop layout (md+) - Amazon Music FireTV style */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.8) 100%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'relative',
          zIndex: 2,
          width: '100%',
          height: '100%',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: 5,
        }}
      >
        {/* Top row: title/artist + close button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ minWidth: 0, maxWidth: '70%' }}>
            {isLoading ? (
              <>
                <Skeleton variant="text" sx={{ fontSize: '2.5rem', backgroundColor: 'rgba(255,255,255,0.2)', width: 320 }} />
                <Skeleton variant="text" sx={{ fontSize: '1.25rem', backgroundColor: 'rgba(255,255,255,0.2)', width: 220 }} />
              </>
            ) : (
              <>
                <Typography
                  variant="h4"
                  sx={{
                    color: '#fff',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {nowPlaying?.title || 'Unknown Title'}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#ccc',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {nowPlaying?.artist || 'Unknown Artist'}
                </Typography>
                {isLive && (
                  <Box sx={{ marginTop: 1 }}>
                    <FlashingLiveIndicator station={station} />
                  </Box>
                )}
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5 }}>
            <IconButton onClick={onClose} sx={{ color: '#fff' }}>
              <ExpandMoreIcon fontSize="large" />
            </IconButton>

            {showUpNext && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 220 }}>
                <Box sx={{ minWidth: 0, textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                    Up Next
                  </Typography>
                  {showUpNextSkeleton ? (
                    <>
                      <Skeleton variant="text" width={120} height={20} sx={{ marginLeft: 'auto' }} />
                      <Skeleton variant="text" width={80} height={16} sx={{ marginLeft: 'auto' }} />
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        sx={{ color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {playingNext.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                      >
                        {playingNext.artist}
                      </Typography>
                    </>
                  )}
                </Box>
                <Box sx={{ width: 56, height: 56, borderRadius: 1, overflow: 'hidden', flexShrink: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                  {showUpNextSkeleton ? (
                    <Skeleton variant="rectangular" width={56} height={56} />
                  ) : (
                    <img
                      src={playingNext.art || 'https://placehold.co/56'}
                      alt={playingNext.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Middle row: large album art */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" sx={{ width: 320, height: 320, borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.2)' }} />
          ) : (
            <Box
              sx={{
                width: 320,
                height: 320,
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
              }}
            >
              <img
                src={nowPlaying?.art || 'https://placehold.co/320'}
                alt={nowPlaying?.title || 'Album Art'}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
          )}
        </Box>

        {/* Bottom block: progress rail (scrubable for on-demand, display-only for AutoDJ tracks, hidden during a live broadcast) + controls */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!isLive && (
            <CustomProgressBar
              key="audio-drawer-rail"
              variant="rail"
              color={isLiveStream ? progressColor : '#868686'}
              initialElapsed={nowPlayingData?.elapsed || 0}
              duration={nowPlayingData?.duration || 1}
            />
          )}

          {/* Fixed height keeps the art/progress rail above from shifting when the spinner
              (smaller than the 80px play button) swaps in during a track change */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, minHeight: 80 }}>
            {loadingTrackId ? (
              <CircularProgress thickness={3} />
            ) : (
              <>
                {!isLiveStream && (
                  <IconButton
                    onClick={() => playPreviousTrack(nowPlayingData?.trackList || [])}
                    disabled={currentTrackIndex <= 0}
                    sx={{ color: '#fff' }}
                  >
                    <SkipPreviousIcon sx={{ fontSize: 48 }} />
                  </IconButton>
                )}

                <IconButton
                  onClick={togglePlayback}
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'primary.main',
                    color: '#000',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  {isPlaying ? (
                    <PauseCircleIcon sx={{ fontSize: 64 }} />
                  ) : (
                    <PlayCircleIcon sx={{ fontSize: 64 }} />
                  )}
                </IconButton>

                {!isLiveStream && (
                  <IconButton
                    onClick={() => playNextTrack(nowPlayingData?.trackList || [])}
                    disabled={
                      !nowPlayingData?.trackList ||
                      currentTrackIndex === null ||
                      currentTrackIndex >= nowPlayingData.trackList.length - 1
                    }
                    sx={{ color: '#fff' }}
                  >
                    <SkipNextIcon sx={{ fontSize: 48 }} />
                  </IconButton>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </SwipeableDrawer>
  );
};

export default AudioDrawer;
